import { withAuthenticationRequired } from '@auth0/auth0-react';
import { MainLayout } from 'components/layouts';
import { TextInput } from 'flowbite-react';
import 'global.css';
import { SearchIcon } from 'components/assets';
import { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import FileListPageContent from 'components/pages/FileList/fileListPageContent';
import debouce from 'lodash.debounce';
import useUser from 'hooks/useUser';
import { setCookie } from '../utils/cookie';
import { useGetCurrentUser } from 'hooks';
import { useSearchParams } from 'react-router-dom';
import {
  documentTypes,
  fileDateSortOptions,
  fileListFilterDefaults,
  fileStatusFilterOptions,
  pageLabels,
} from 'utils/constants';

const getDocumentTypeFromParams = searchParams => {
  const kind = searchParams.get('kind');
  return Object.values(documentTypes).includes(kind) ? kind : documentTypes.BMA;
};

const getCurrentPageFromParams = searchParams => {
  const page = parseInt(searchParams.get('page'), 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};

const getFiltersFromParams = searchParams => ({
  ...fileListFilterDefaults,
  startDate: searchParams.get('startDate') || fileListFilterDefaults.startDate,
  endDate: searchParams.get('endDate') || fileListFilterDefaults.endDate,
  status: searchParams.get('status') || fileListFilterDefaults.status,
  sortDirection:
    searchParams.get('sortDirection') || fileListFilterDefaults.sortDirection,
});

const FileList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  const searchQuery = searchParams.get('query') || '';
  const documentType = getDocumentTypeFromParams(searchParams);
  const filters = getFiltersFromParams(searchParams);
  const currentPage = getCurrentPageFromParams(searchParams);
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [enableFetching, setEnableFetching] = useState(false);
  const { userToken } = useUser();

  const updateSearchParams = useCallback(
    (updates, { resetPage = true } = {}) => {
      const nextParams = new URLSearchParams(searchParamsRef.current);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === '' || value === null || value === undefined) {
          nextParams.delete(key);
        } else {
          nextParams.set(key, value);
        }
      });

      if (resetPage) {
        nextParams.set('page', '1');
      }

      searchParamsRef.current = nextParams;
      setSearchParams(nextParams);
    },
    [setSearchParams],
  );

  const debouncedResults = useMemo(() => {
    return debouce(value => {
      updateSearchParams({ query: value }, { resetPage: true });
    }, 700);
  }, [updateSearchParams]);

  const handleFilterChange = e => {
    const { name, value } = e.target;
    updateSearchParams({ [name]: value }, { resetPage: true });
  };

  const handleSearchChange = e => {
    setSearchInput(e.target.value);
    debouncedResults(e.target.value);
  };

  const handleDocumentTypeChange = kind => {
    updateSearchParams({ kind }, { resetPage: true });
  };

  const handlePageChange = page => {
    updateSearchParams({ page: String(page) }, { resetPage: false });
  };

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (userToken && userToken.length > 0) {
      setEnableFetching(true);
    }
  }, [userToken]);

  const { data } = useGetCurrentUser(userToken, enableFetching);

  useEffect(() => {
    if (data && data.data.id) {
      setCookie('userId', data.data.id);
    }
  }, [data]);
  return (
    <MainLayout>
      <div className="container mx-auto pt-8">
        <div className="mb-6 flex flex-wrap gap-2" data-testid="test-file-kind-toggle">
          <button
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              documentType === documentTypes.BMA
                ? 'bg-blue-700 text-white'
                : 'border border-gray-200 bg-white text-gray-700'
            }`}
            data-testid="test-kind-bma"
            onClick={() => handleDocumentTypeChange(documentTypes.BMA)}
            type="button"
          >
            {pageLabels.FILE_LIST.FILTER_BMA}
          </button>
          <button
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              documentType === documentTypes.APPRAISAL
                ? 'bg-blue-700 text-white'
                : 'border border-gray-200 bg-white text-gray-700'
            }`}
            data-testid="test-kind-appraisal"
            onClick={() => handleDocumentTypeChange(documentTypes.APPRAISAL)}
            type="button"
          >
            {pageLabels.FILE_LIST.FILTER_APPRAISAL}
          </button>
        </div>
        <div className="grid gap-4 mb-10 sm:grid-cols-2 lg:grid-cols-5">
          <TextInput
            data-testid="test-search-input"
            rightIcon={SearchIcon}
            id="base"
            type="text"
            sizing="md"
            placeholder="Search by File Number or Address"
            value={searchInput}
            onChange={handleSearchChange}
            autoFocus
          />
          <TextInput
            data-testid="test-start-date-filter"
            id="startDate"
            name="startDate"
            type="date"
            sizing="md"
            value={filters.startDate}
            onChange={handleFilterChange}
          />
          <TextInput
            data-testid="test-end-date-filter"
            id="endDate"
            name="endDate"
            type="date"
            sizing="md"
            value={filters.endDate}
            onChange={handleFilterChange}
          />
          <select
            aria-label="Filter by status"
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900"
            data-testid="test-status-filter"
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            {fileStatusFilterOptions.map(option => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Sort by date"
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900"
            data-testid="test-sort-date-filter"
            name="sortDirection"
            value={filters.sortDirection}
            onChange={handleFilterChange}
          >
            {fileDateSortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <FileListPageContent
          searchQuery={searchQuery}
          documentType={documentType}
          filters={filters}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </MainLayout>
  );
};
export default withAuthenticationRequired(FileList);
